import { computed, onMounted, reactive, ref, watch } from "vue";
import xlsx from "xlsx";
import feather from "feather-icons";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { useStore } from "@/store";
import Swal from "sweetalert2";
import { customSwalMessageBuilder } from "./utils/customSwalMessageBuilder";
import { useRoute, useRouter } from "vue-router";
import { authGetterService } from "@/services/auth/auth-getter-service";
import { handleErrorResponse } from "@/services/auth/responseHandler/handle-response-service";
import {
  lenderDashToQueueMapper,
  defaultOrder
} from "@/utils/LendginEnums/lending-dash-list-order-type";

export const loanClosureReviewDashboard = () => {
  const { buildSwalSuccess, buildSwalHtmlError } = customSwalMessageBuilder();
  const tableRef = ref();
  const tabulator = ref();
  const filter = reactive({
    field: "fullname",
    type: "like",
    value: "",
    from: "",
    to: ""
  });
  const fromDate = reactive({
    field: "from",
    type: ">=",
    value: ""
  });
  const toDate = reactive({
    field: "to",
    type: "<=",
    value: ""
  });
  const newStatus = ["Assigned"];

  const approvedStatus = ["Approved"];

  const declinedStatus = ["Declined"];

  const completedStatus = ["Completed"];

  const approvalRejectionDataObject = reactive({
    uuid: "",
    note: "",
    title: "",
    type: ""
  });

  const route = useRoute();
  const router = useRouter();
  const path = computed(() => route.path);
  const store = useStore();
  const assessmentUuidToNavigate = ref("");
  const reqForAssessmentDetailsNavigationCounter = computed(
    () =>
      store.state.lnClosureReviewsBucket
        .reqForAssessmentDetailsNavigationCounter
  );

  watch(
    () => reqForAssessmentDetailsNavigationCounter.value,
    () => {
      if (assessmentUuidToNavigate.value !== "") {
        redirectToAssesmentDetailsPage(assessmentUuidToNavigate.value);
      }
    }
  );

  function redirectToAssesmentDetailsPage(assessmentUuidToNavigate) {
    router.push(
      router.resolve({
        name: "loan-assessment-detail",
        params: { ln_assessment_uuid: assessmentUuidToNavigate }
      })
    );
  }

  const adminUser = computed(() => store.state.auth.loggedUser);

  const assessmentBucketList = computed(
    () => store.state.lnClosureReviewsBucket.assessmentBucketList
  );

  const activityStats = computed(
    () => store.state.lnClosureReviewsBucket.activityStats
  );

  const recentlyAssessedAssessments = computed(
    () => store.state.lnClosureReviewsBucket.recentlyAssessedAssessments
  );

  const assessmentBucketQueryParam = computed(
    () => store.state.lnClosureReviewsBucket.assessmentBucketQueryParam
  );

  const assessmentBucketAlertMessage = computed(
    () => store.state.lnClosureReviewsBucket.assessmentBucketAlertMessage
  );

  const toastMessage = ref("");

  watch(
    () => assessmentBucketAlertMessage.value,
    message => {
      if (message !== "") {
        toastMessage.value = message;
        store.commit("globalToastMessage/incrementToastReqCounter");
      }
    }
  );

  const assessmentBucketListUpdateCounter = computed(
    () => store.state.lnClosureReviewsBucket.assessmentBucketListUpdateCounter
  );

  watch(
    () => assessmentBucketListUpdateCounter.value,
    () => {
      init();
      reInitOnResizeWindow();
      tabulator.value.replaceData();
    }
  );

  const assessmentBucketSuccessMessage = computed(
    () => store.state.lnClosureReviewsBucket.assessmentBucketSuccessMessage
  );

  watch(
    () => assessmentBucketSuccessMessage.value,
    message => {
      if (message) {
        Swal.fire(buildSwalSuccess(message)).then(confirmed => {
          if (confirmed.isConfirmed) {
            store.commit("lnClosureReviewsBucket/clearMessages");
          }
        });
      }
    }
  );

  const assessmentBucketErrorMessage = computed(
    () => store.state.lnClosureReviewsBucket.assessmentBucketErrorMessage
  );

  watch(
    () => assessmentBucketErrorMessage.value,
    message => {
      if (message) {
        Swal.fire(buildSwalHtmlError(message)).then(confirmed => {
          if (confirmed.isConfirmed) {
            store.commit("lnClosureReviewsBucket/clearMessages");
          }
        });
      }
    }
  );

  function init() {
    store.commit(
      "lnClosureReviewsBucket/setAssessorRoleType",
      path.value.split("/")[2]
    );
    store.dispatch("lnClosureReviewsBucket/getAdminPreferencesForDashList");
    store.dispatch("lnClosureReviewsBucket/getRecentAssessmentsOfAuthLender");
  }

  function onApprovalSubmit() {
    store.commit("main/setFullPageLoading");
    store.dispatch(
      "lnClosureReviewsBucket/sendForApproval",
      approvalRejectionDataObject
    );
    cash("#approval-rejection-modal").modal("hide");
  }

  function onChangeAssessmentFetchOrder() {
    store.commit("main/setFullPageLoading");
    let dashListOrder =
      lendingDashListOrderType.value !== null
        ? lendingDashListOrderType.value
        : defaultOrder;
    dashListOrder[lenderDashToQueueMapper[path.value.split("/")[2]]] =
      assessmentFetchSelectionType.value;
    store.dispatch(
      "lnClosureReviewsBucket/setAdminPreferencesForDashList",
      dashListOrder
    );
  }

  function onFetchAssessmentReqSubmit() {
    store.commit("main/setFullPageLoading");
    store.dispatch(
      "lnClosureReviewsBucket/fetchAssessment",
      assessmentFetchSelectionType.value
    );
  }

  function onRejectionSubmit() {
    store.commit("main/setFullPageLoading");
    store.dispatch(
      "lnClosureReviewsBucket/sendForRejection",
      approvalRejectionDataObject
    );
    cash("#approval-rejection-modal").modal("hide");
  }

  function getAccessBucketsAction() {
    store.dispatch("lnClosureReviewsBucket/getAccessBucketList");
  }

  function getIconNameAsPerRecordStatus(status) {
    if ("Assigned" === status) return "file-plus";
    else if ("In Progress" === status) return "clock";
    else if ("On Hold" === status) return "pause-circle";
    else if ("Request Decline" === status) return "message-circle";
    else if ("Completed" === status) return "user-check";
    else if ("Declined" === status) return "x-circle";
    else if ("In Review" === status) return "search";
    else if ("Approved" === status) return "check";
    else if ("Pre Disbursal Check" === status) return "loader";
    else if ("Pending Conversion" === status) return "loader";
    else if ("Converted" === status) return "check-square";
  }

  function getStartLabelAsPerAdminRole() {
    return "Start Closure Review";
  }

  function onClickResolveAppDetailsAndReRoute(applicationId) {
    store.dispatch(
      "lnAppDtsRouter/resolveAndReRouteLnAssessmentDtsPage",
      applicationId
    );
  }

  function compareExistingBucketWithNewListAndBuildAppropriateTokenMessageObject(
    newListData
  ) {
    let newRecord = true;
    if (assessmentBucketList.value.length === newListData.length) {
      newRecord = false;
      for (let item of newListData) {
        if (!assessmentBucketList.value.map(a => a.uuid).includes(item.uuid)) {
          newRecord = true;
          break;
        }
      }
    }

    if (newRecord) {
      return {
        message: "Successfully updated the Bucket",
        type: "success"
      };
    }
    return {
      message:
        "No Record to fetch or bucket limit might have reached ! New changes if any on existing records has been updated.",
      type: "success"
    };
  }

  const assessmentFetchSelectionType = ref("priority");

  const lendingDashListOrderType = computed(
    () => store.state.lnClosureReviewsBucket.lendingDashListOrderType
  );

  watch(
    () => lendingDashListOrderType.value,
    () => {
      if (lendingDashListOrderType.value) {
        assessmentFetchSelectionType.value =
          lendingDashListOrderType.value[
            lenderDashToQueueMapper["lending-agent"]
          ];
      }
    }
  );

  const settingGetCustomerConfigs = computed(
    () => store.state.lnClosureReviewsBucket.settingGetCustomerConfigs
  );

  function compareExistingBucketWithNewListAndDisplayAppropriateAlert(
    listData
  ) {
    store.dispatch(
      "globalToastMessage/showSimpleToast",
      compareExistingBucketWithNewListAndBuildAppropriateTokenMessageObject(
        listData
      )
    );
  }
  store.commit("lnClosureReviewsBucket/setSettingGetCustomerConfigs");
  onMounted(() => {
    init();
    initTabulator();
    reInitOnResizeWindow();
  });

  const initTabulator = () => {
    tabulator.value = new Tabulator(tableRef.value, {
      ajaxURL: process.env.VUE_APP_BASE_URL + "/getLoanClosureReviewsForBucket",
      ajaxConfig: {
        method: "GET", //set request type to GET
        headers: authGetterService.getAuthHeader()
      },
      ajaxRequesting: function(url, params) {
        store.commit("lnClosureReviewsBucket/setAssessmentBucketQueryParam", {
          params
        });
        //url - the URL of the request
        //params - the parameters passed with the request
      },
      ajaxResponse: function(url, params, response) {
        //url - the URL of the request
        //params - the parameters passed with the request
        //response - the JSON object returned in the body of the response.

        if (assessmentBucketListUpdateCounter.value > 0) {
          compareExistingBucketWithNewListAndDisplayAppropriateAlert(
            response.data
          );
        }

        store.commit(
          "lnClosureReviewsBucket/setAssessmentBucketList",
          response.data
        );
        return response.data; //return the response data to tabulator
      },
      filterMode: "remote",
      sortMode: "remote",
      printAsHtml: true,
      printStyled: true,
      reactiveData: true, //enable reactive data
      layout: "fitColumns",
      responsiveLayout: "collapse",
      placeholder: "No matching records found",
      columns: [
        {
          formatter: "responsiveCollapse",
          width: 40,
          minWidth: 30,
          hozAlign: "center",
          resizable: false,
          headerSort: false
        },

        // For HTML table
        {
          title: "CUSTOMER",
          minWidth: 150,
          responsive: 0,
          field: "fullname",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            return `<div>
                <div class="font-medium whitespace-nowrap"><a target="_blank" href="https://fundo.com.au/adminpanel/payments/schedules/${
                  cell.getData().application_id
                }"><u>${cell.getData().fullname}</u></a></div>
                <div class="text-gray-600 text-xs whitespace-nowrap"><i>${
                  cell.getData().email
                }</i></div>
                <div class="text-gray-600 text-xs whitespace-nowrap">${
                  cell.getData().mobile
                }</div>
              </div>`;
          }
        },
        {
          title: "AMOUNT",
          minWidth: 100,
          field: "requested_loan_amount",
          hozAlign: "center",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            return `<div>
                <div class="font-medium whitespace-nowrap">${
                  cell.getData().loan.total_amount
                }</div>
              </div>`;
          }
        },
        {
          title: "Requested At",
          minWidth: 100,
          field: "created_at",
          hozAlign: "center",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            return `<div>
                <div class="font-medium whitespace-nowrap">${
                  cell.getData().created_at
                }</div>
                </div>`;
          }
        },
        {
          title: "STATUS",
          minWidth: 100,
          field: "status",
          hozAlign: "center",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            return `<div class="flex items-center lg:justify-center ${
              newStatus.includes(cell.getData().status)
                ? "text-theme-9"
                : approvedStatus.includes(cell.getData().status) ||
                  completedStatus.includes(cell.getData().status)
                ? "text-blue-400"
                : declinedStatus.includes(cell.getData().status)
                ? "text-red-600"
                : "text-yellow-400"
            }">
                <i data-feather="${getIconNameAsPerRecordStatus(
                  cell.getData().status
                )}" class="w-4 h-4 mr-2"></i> ${cell.getData().status}
              </div>`;
          }
        },
        {
          title: "ACTIONS",
          minWidth: 100,
          field: "ACTIONS",
          hozAlign: "center",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            const a = cash(
              `<div class="flex lg:justify-left items-left"></div>`
            );
            const c = cash(`<div></div>`);
            if (cell.getData().status !== "Converted") {
              cash(a)
                .append(`<a class="flex items-left text-blue-400 mr-3" href="javascript:;">
                  <i data-feather="play" class="w-4 h-4 mr-1"></i> ${
                    cell.getData().status === "On Hold"
                      ? "Resume"
                      : getStartLabelAsPerAdminRole()
                  }
                </a>`);
              cash(cash(a).children()[0]).on("click", function() {
                onClickResolveAppDetailsAndReRoute(
                  cell.getData().application_id
                );
              });
              cash(c).append(a[0]);
            }
            return c[0];
          }
        }
      ]
    });

    tabulator.value.on("dataLoadError", function(errorResponse) {
      handleErrorResponse(errorResponse);
    });

    tabulator.value.on("renderComplete", function() {
      feather.replace({
        "stroke-width": 1.5
      });
    });
  };

  // Redraw table onresize
  const reInitOnResizeWindow = () => {
    window.addEventListener("resize", () => {
      tabulator.value.redraw();
      feather.replace({
        "stroke-width": 1.5
      });
    });
  };

  // Filter function
  const onFilter = () => {
    const filterBuilder = [];
    filterBuilder.push({
      field: filter.field,
      type: filter.type,
      value: filter.value
    });
    if (fromDate.value !== "") {
      filterBuilder.push({
        field: fromDate.field,
        type: fromDate.type,
        value: fromDate.value
      });
    }
    if (toDate.value !== "") {
      filterBuilder.push({
        field: toDate.field,
        type: toDate.type,
        value: toDate.value
      });
    }

    tabulator.value.setFilter(filterBuilder);
  };

  // On reset filter
  const onResetFilter = () => {
    filter.field = "fullname";
    filter.type = "like";
    filter.value = "";

    fromDate.field = "from";
    fromDate.type = ">=";
    fromDate.value = "";

    toDate.field = "to";
    toDate.type = "<=";
    toDate.value = "";
    onFilter();
  };

  // Export
  const onExportCsv = () => {
    tabulator.value.download("csv", "data.csv");
  };

  const onExportJson = () => {
    tabulator.value.download("json", "data.json");
  };

  const onExportXlsx = () => {
    const win = window;
    win.XLSX = xlsx;
    tabulator.value.download("xlsx", "data.xlsx", {
      sheetName: "Products"
    });
  };

  const onExportHtml = () => {
    tabulator.value.download("html", "data.html", {
      style: true
    });
  };

  // Print
  const onPrint = () => {
    tabulator.value.print();
  };

  const onExport = () => {
    store.commit("main/setFullPageLoading");
    store.dispatch(
      "lnClosureReviewsBucket/requestForExportingList",
      assessmentBucketQueryParam.value
    );
  };

  return {
    adminUser,
    tableRef,
    fromDate,
    toDate,
    filter,
    onFilter,
    onResetFilter,
    onExportCsv,
    onExportJson,
    onExportXlsx,
    onExportHtml,
    onPrint,
    onExport,
    path,
    assessmentBucketList,
    activityStats,
    recentlyAssessedAssessments,
    getAccessBucketsAction,
    approvalRejectionDataObject,
    onFetchAssessmentReqSubmit,
    onApprovalSubmit,
    onRejectionSubmit,
    init,
    assessmentFetchSelectionType,
    settingGetCustomerConfigs,
    onChangeAssessmentFetchOrder
  };
};
